import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import RightRail, { Header, Content, TripleAction, Footer } from '../../../libs/gs-component-right-rail';
import { H4 } from '@xo-union/tk-ui-typography';
import { SingleSelectGroup } from '@xo-union/tk-component-switches';
import { RadioOptionsList } from '../../ButtonPillDropdown/Dropdown';
import { EventsQuery } from '../../../generated/graphql';
import styles from './styles.scss';
import { EVENTS_ALL_OPTION, RSVP_STATUS_OPTIONS, RSVP_STATUS_ALL_OPTION } from '.';
import { useFilteredHouseholds } from './useFilteredHouseholds';
import { PillDropdownOptions } from '../../ButtonPillDropdown';

interface MobileGuestFiltersProps {
  filters: {
    event: string | null;
    rsvpStatus: string | null;
  };
  events: EventsQuery['events'];
  setFilterCriteria: (rsvpStatusString: string, eventNameString: string) => void;
  filtersPath: string;
}

export const MobileGuestFilters = ({ events, setFilterCriteria, filters, filtersPath }: MobileGuestFiltersProps) => {
  const history = useHistory();
  const match = useRouteMatch(filtersPath);
  const { filteredHouseholds: predicted, setFilterCriteria: setPredictedFilterCriteria } = useFilteredHouseholds();
  const [eventFilter, setEventFilter] = useState<string>(filters.event || EVENTS_ALL_OPTION);
  const [savedEventFilter, setSavedEventFilter] = useState<string>(filters.event || EVENTS_ALL_OPTION);
  const [rsvpStatusFilter, setRsvpStatusFilter] = useState<string>(filters.rsvpStatus || RSVP_STATUS_ALL_OPTION);
  const [savedRsvpStatusFilter, setSavedRsvpStatusFilter] = useState<string>(
    filters.rsvpStatus || RSVP_STATUS_ALL_OPTION
  );

  const eventsOptionList: PillDropdownOptions = useMemo(
    () =>
      events?.reduce(
        (list: PillDropdownOptions, evt: EventsQuery['events'][0]) =>
          evt?.name ? [...list, { text: evt.name, value: evt.id }] : list,
        [{ text: EVENTS_ALL_OPTION, value: EVENTS_ALL_OPTION }]
      ),
    [events]
  );

  const eventIdToNameMap: Record<string, string> = useMemo(
    () => events?.reduce((obj, evt: EventsQuery['events'][0]) => ({ ...obj, [evt.id]: evt.name }), {}),
    [events]
  );

  const savedEventFilterNameString = eventIdToNameMap[savedEventFilter];

  const onSetFilters = () => {
    const eventValue = eventIdToNameMap[eventFilter] || EVENTS_ALL_OPTION;
    const rsvpValue = rsvpStatusFilter || RSVP_STATUS_ALL_OPTION;
    setFilterCriteria(rsvpValue, eventValue);
    setSavedEventFilter(eventFilter);
    setSavedRsvpStatusFilter(rsvpValue);
    closeRail();
  };

  const showRail = match?.isExact || false;
  const closeRail = () => history.goBack();

  useEffect(() => {
    setPredictedFilterCriteria(
      rsvpStatusFilter === RSVP_STATUS_ALL_OPTION ? null : rsvpStatusFilter,
      eventFilter === EVENTS_ALL_OPTION ? null : eventIdToNameMap[eventFilter]
    );
  }, [eventFilter, eventIdToNameMap, rsvpStatusFilter, setPredictedFilterCriteria]);

  useEffect(() => {
    // reset local state if filters were not saved
    if (!showRail) {
      if (eventFilter !== savedEventFilter) {
        setEventFilter(savedEventFilter);
      }
      if (rsvpStatusFilter !== savedRsvpStatusFilter) {
        setRsvpStatusFilter(savedRsvpStatusFilter);
      }
    }
  }, [eventFilter, rsvpStatusFilter, savedEventFilter, savedRsvpStatusFilter, showRail]);

  useEffect(() => {
    // reset local state if filters were cleared outside of this component
    if (!showRail) {
      if (filters?.event !== savedEventFilterNameString) {
        setEventFilter(EVENTS_ALL_OPTION);
        setSavedEventFilter(EVENTS_ALL_OPTION);
      }
      if (filters?.rsvpStatus !== savedRsvpStatusFilter) {
        setRsvpStatusFilter(RSVP_STATUS_ALL_OPTION);
        setSavedRsvpStatusFilter(RSVP_STATUS_ALL_OPTION);
      }
    }
  }, [filters, savedEventFilterNameString, savedRsvpStatusFilter, showRail]);

  const guestCount = predicted.length;
  const actionButtonText =
    guestCount && guestCount > 0 ? (guestCount === 1 ? 'See Guest' : `See ${guestCount} Guests`) : 'See Guests';
  const actionDisabled = savedEventFilter === eventFilter && savedRsvpStatusFilter === rsvpStatusFilter;

  return (
    <RightRail open={showRail} overlay={false} className={styles.mobileRightRail}>
      <Header onClose={closeRail}>Filters</Header>
      <Content>
        <div className={styles.mobileFilterSection}>
          <H4 as="h1">Event</H4>
          <SingleSelectGroup value={eventFilter} onValueChange={setEventFilter}>
            <RadioOptionsList options={eventsOptionList} />
          </SingleSelectGroup>
        </div>
        <div className={styles.mobileFilterSection}>
          <H4 as="h1">RSVP Status</H4>
          <SingleSelectGroup value={rsvpStatusFilter} onValueChange={setRsvpStatusFilter}>
            <RadioOptionsList options={RSVP_STATUS_OPTIONS} />
          </SingleSelectGroup>
        </div>
      </Content>
      <Footer>
        <TripleAction primaryText={actionButtonText} primaryOnClick={onSetFilters} primaryDisabled={actionDisabled} />
      </Footer>
    </RightRail>
  );
};
